import { currentLocale } from '@/i18n'
import { withLocale } from '@/i18n/shared-locale'
import { LANDING_URL, LOGISTICS_URL } from '@/utils/config'

/**
 * Where the auth app may send a signed-in person, and with what.
 *
 * `?redirect=` carries the access token to the page it names, so it must never name a stranger's
 * site: a crafted sign-in link would hand the token to whoever wrote it. Only this app, the landing
 * page and the logistics app qualify, the origins this build was configured with.
 */
function originOf(url: string): string | null {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

function trustedOrigins(): Set<string> {
  const origins = new Set<string>([window.location.origin])
  for (const configured of [LANDING_URL, LOGISTICS_URL]) {
    const origin = originOf(configured)
    if (origin) origins.add(origin)
  }

  return origins
}

/** The `redirect` query value as one string; the router may hand back an array or null. */
export function redirectParam(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value

  return typeof raw === 'string' && raw !== '' ? raw : undefined
}

/** The redirect as an absolute URL when it is one of ours, otherwise null. */
export function trustedRedirect(value: unknown): string | null {
  const raw = redirectParam(value)
  if (!raw) return null

  try {
    const url = new URL(raw, window.location.origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

    return trustedOrigins().has(url.origin) ? url.toString() : null
  } catch {
    return null
  }
}

/**
 * The target with the token added as a query parameter, whatever query it already has, and the
 * language chosen here, so the app the person lands in speaks the same one.
 */
export function withToken(target: string, token: string): string {
  const url = new URL(target)
  url.searchParams.set('token', token)

  return withLocale(url.toString(), currentLocale(), window.location.origin)
}
